import { useEffect, useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getFromSessionStorage, setToSessionStorage } from '@ui/utils'

interface ScrollToBottomButtonProps {
	readonly parentRef: React.RefObject<HTMLDivElement | undefined>;
	readonly origin: 'chat' | 'artifact';
}

function ScrollToBottomButton({ parentRef, origin }: ScrollToBottomButtonProps) {
	const [displayScrollButton, setDisplayScrollButton] = useState(false)

	const sessionKey = `${origin}-isAutoScrollEnabled`
	const isAutoScrollEnabled = getFromSessionStorage(sessionKey, 'true') === 'true'

	useEffect(() => {
		if (!parentRef.current) {
			return
		}

		// Create a MutationObserver to watch for content changes
		const observer = new MutationObserver(() => {
			if (isAutoScrollEnabled) {
				scrollToBottom()
			}
		})

		// Start observing the parent element for changes
		observer.observe(parentRef.current, {
			childList: true,
			subtree: true,
			characterData: true
		})

		return () => {
			observer.disconnect()
		}
	}, [parentRef, isAutoScrollEnabled])

	function scrollToBottom() {
		if (parentRef.current) {
			parentRef.current.scrollTo({
				left: 0,
				top: parentRef.current.scrollHeight,
				behavior: 'smooth'
			})
		}
	}

	useEffect(() => {
		function handleScroll() {
			if (parentRef.current) {
				const currentHeight = parentRef.current.getClientRects()?.[0]?.height

				const { scrollTop, scrollHeight } = parentRef.current

				if (scrollTop + currentHeight < scrollHeight - 100) {
					setDisplayScrollButton(true)
				}

				if (scrollTop + currentHeight > scrollHeight - 10) {
					setDisplayScrollButton(false)
				}
			}
		}

		if (parentRef.current) {
			parentRef.current.addEventListener('scroll', handleScroll)
		}

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [parentRef])

	useEffect(() => {
		function handleWheel() {
			if (parentRef.current) {
				const currentHeight = parentRef.current.getClientRects()?.[0]?.height

				const { scrollTop, scrollHeight } = parentRef.current

				if (scrollTop + currentHeight < scrollHeight - 10) {
					setToSessionStorage(sessionKey, 'false')
				} else {
					setToSessionStorage(sessionKey, 'true')
				}
			}
		}

		if (parentRef.current) {
			parentRef.current.addEventListener('wheel', handleWheel)
		}

		return () => {
			window.removeEventListener('wheel', handleWheel)
		}
	}, [parentRef])

	function handleScrollToBottomButton() {
		setToSessionStorage(sessionKey, 'true')
		scrollToBottom()
	}

	return (
		<button
			type="button"
			id={ `${origin}-scrollbutton` }
			className={ `absolute flex items-center justify-center  ${origin === 'artifact' ? 'bottom-32 right-[5%]' : 'bottom-16 right-[10%]'} bottom-16 bg-white hover:bg-gray-100
			rounded-full w-10 h-10 border border-solid border-primary-light transition-all opacity-100 pointer-events-auto ${displayScrollButton ? '' : 'hidden'}` }
			onClick={ handleScrollToBottomButton }
		>
			<FontAwesomeIcon icon="arrow-down" className="w-3 text-gray-500" />
		</button>
	)
}

export default ScrollToBottomButton
